import React, {
  useState,
  useEffect
} from 'react';
import PropTypes from 'prop-types';

import {
  Flex,
  Label,
  Modal,
  Select,
  useNotification
} from '@sopranodesign/pulse-components';
import { useQueryClient } from '@sopranodesign/pulse-hooks';

import {
  resyncAll,
  deleteCategory,
  resyncCategory,
  saveCategories,
  saveZendeskTags,
  createZendeskTag,
  fetchZendeskTags,
  recrawlZendeskArticle,
  fetchZendeskAvailableTags
} from '../api';
import WarningMessageBody from './WarningMessageBody';
import TagModal from './TagModal';
import { useTagManager } from './useTagManager';

const ZendeskModalManager = ({
  activeModal,
  modalProps,
  closeModal,
  categories,
  setErrorIds,
  setCategoryLoadingIds,
  setArticleLoadingIds,
  setIsSyncingAll
}) => {
  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);

  const {
    tags,
    setTags,
    selectedTags,
    setSelectedTags,
    handleTagChange,
  } = useTagManager({
    scope: 'generative_ai_zendesk_articles',
    createTagFn: createZendeskTag
  });

  const handleSaveCategories = async () => {
    try {
      const payload = selectedCategories.map(cat => ({ id: cat.value, name: cat.label }));
      await saveCategories(payload);
      queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/'] });
      showSuccess('Articles fetched successfully!');
      closeModal();
      setSelectedCategories([]);
    } catch (error) {
      showError(`Failed to fetch articles: ${error.message}`);
    }
  };

  const confirmationActions = {
    delete: {
      header: 'Confirm Delete Category',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to delete the category "${modalProps.category?.name}"?`}
          contentBody="All synced articles will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Delete',
      handleConfirm: async () => {
        const categoryId = modalProps.category.id;
        // optional: mark category as loading during delete
        setCategoryLoadingIds(prev => ({ ...prev, [categoryId]: true }));
        try {
          await deleteCategory(categoryId);
          showSuccess(`Category "${modalProps.category.name}" delete requested`);
          // Invalidate list so removed category disappears when backend reports
          queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/'] });
          closeModal();
        } catch (error) {
          showError(`Failed to delete category: ${error.message}`);
        } finally {
          setCategoryLoadingIds(prev => ({ ...prev, [categoryId]: false }));
        }
      }
    },
    resync: {
      header: 'Confirm Resync Category',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to resync the category “${modalProps.category?.name}”?`}
          contentBody="This will refresh its content."
        />
      ),
      confirmText: 'Resync',
      handleConfirm: async () => {
        const categoryId = modalProps.category.id;
        // NEW: mark loading for this category
        setCategoryLoadingIds(prev => ({ ...prev, [categoryId]: true }));
        try {
          await resyncCategory(categoryId);
          showSuccess(`Category resync requested.`);
          // Optional: invalidate category articles under this category if needed
          queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/articles', categoryId] });
          closeModal();
        } catch (error) {
          showError(`Failed to resync category: ${error.message}`);
        } finally {
          setCategoryLoadingIds(prev => ({ ...prev, [categoryId]: false }));
        }
      }
    },
    resyncAll: {
      header: 'Confirm Resync All Categories',
      body: (
        <WarningMessageBody
          contentHeader="Are you sure you want to resync all categories?"
          contentBody="This will refresh all categories content."
        />
      ),
      confirmText: 'Resync All',
      handleConfirm: async () => {
        setIsSyncingAll(true);
        try {
          await resyncAll();
          showSuccess(`Categories resync requested.`);
          // Refresh lists
          queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/'] });
          closeModal();
        } catch (error) {
          showError(`Failed to resync categories: ${error.message}`);
        } finally {
          setIsSyncingAll(false);
        }
      }
    },
    recrawl: {
      header: 'Confirm Recrawl',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to recrawl the article “${modalProps.article?.title}”?`}
          contentBody="This will update its content."
        />
      ),
      confirmText: 'Recrawl',
      handleConfirm: async () => {
        const articleId = modalProps.article.id;
        const categoryId = modalProps.categoryId;

        // Clear any previous error for this article and mark loading
        setErrorIds(prev => {
          const next = { ...prev };
          delete next[articleId];
          return next;
        });
        setArticleLoadingIds(prev => ({ ...prev, [articleId]: true }));

        try {
          queryClient.setQueriesData({ queryKey: ['generative-ai/zendesk/articles', categoryId] }, (oldData) => {
            if (!oldData) { return oldData; }
            const cloned = JSON.parse(JSON.stringify(oldData));
            const list = cloned?.data?.data ?? cloned?.data ?? cloned?.data?.articles ?? [];
            const arr = Array.isArray(list) ? list : [];
            arr.forEach(a => {
              if (a.id === articleId) {
                a.status = 'Pending';
              }
            });
            // Attempt to set back in the same shape
            if (cloned?.data?.data) { cloned.data.data = arr; }
            else if (cloned?.data) { cloned.data = arr; }
            return cloned;
          });
        } catch {
          // If cache shape differs, we silently ignore; UI will still show spinner
        }

        try {
          await recrawlZendeskArticle(articleId);
          showSuccess(`Article recrawl requested`);
          queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/articles', categoryId] });
        } catch (error) {
          if (error.response?.status >= 500) {
            setErrorIds(prev => ({ ...prev, [articleId]: 'Failed to recrawl the article.' }));
          }
          showError(`Failed to recrawl article: ${error.message}`);
        } finally {
          setArticleLoadingIds(prev => ({ ...prev, [articleId]: false }));
          closeModal();
        }
      }
    }
  };

  useEffect(() => {
    const openAndLoadTags = async () => {
      if (activeModal === 'tags' && modalProps?.article?.id) {
        try {
          const availableResp = await fetchZendeskAvailableTags();
          const availableTags = availableResp?.data?.data ?? [];
          setTags(availableTags);

          const assignedResp = await fetchZendeskTags(modalProps.article.id);
          const tagList = assignedResp?.data?.data ?? [];
          const ids = tagList.map(t => t.id);

          setSelectedTags(ids);
          setIsTagModalOpen(true);
        } catch (error) {
          showError(error.message || 'Failed to load tags.');
          setIsTagModalOpen(true);
        }
      }
    };

    openAndLoadTags();

    // Cleanup when switching away or closing
    return () => {
      if (activeModal !== 'tags') {
        setSelectedTags([]);
        setIsTagModalOpen(false);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeModal, modalProps?.article?.id]);

  if (!activeModal) {
    return null;
  }

  switch (activeModal) {
    case 'category':
      return (
        <Modal
          header="Help Center Categories"
          handleCancel={closeModal}
          handleConfirm={handleSaveCategories}
          confirmText="Select"
          cancelText="Cancel"
          disableConfirmButton={selectedCategories.length === 0}
          body={
            <Flex col gap="sm">
              <Label htmlFor="categorySelect">Category</Label>
              <Select
                id="categorySelect"
                value={selectedCategories}
                onChange={setSelectedCategories}
                options={categories.map(cat => ({ label: cat.name, value: cat.id }))}
                placeholder="Select a category"
                isMulti
              />
            </Flex>
          }
        />
      );

    case 'tags':
      return (
        <TagModal
          isVisible={isTagModalOpen}
          header="Zendesk Article Tags"
          onCancel={() => {
            setIsTagModalOpen(false);
            setSelectedTags([]);
            closeModal();
          }}
          onConfirm={async () => {
            try {
              await saveZendeskTags({
                zendeskId: modalProps.article.id,
                groupIds: selectedTags
              });

              // Keep cache in sync for the article list under the category
              queryClient.invalidateQueries({ queryKey: ['generative-ai/zendesk/articles', modalProps.categoryId] });

              setIsTagModalOpen(false);
              setSelectedTags([]);
              showSuccess('Tags assigned successfully!');
              closeModal();
            } catch (error) {
              showError(error.message || 'Failed to assign tags.');
            }
          }}
          tags={tags}
          selectedTags={selectedTags}
          handleTagChange={handleTagChange}
        />
      );

    case 'confirmation': {
      const config = confirmationActions[modalProps.type];
      return (
        <Modal
          header={config.header}
          body={config.body}
          cancelText="Cancel"
          confirmText={config.confirmText}
          handleCancel={config.handleCancel ?? closeModal}
          handleConfirm={config.handleConfirm}
        />
      );
    }

    default:
      return null;
  }
};

ZendeskModalManager.propTypes = {
  activeModal: PropTypes.string,
  modalProps: PropTypes.shape({
    categoryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    category: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string
    }),
    article: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      title: PropTypes.string
    })
  }),
  closeModal: PropTypes.func.isRequired,
  categories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string
    })
  ).isRequired,
  setErrorIds: PropTypes.func.isRequired,
  setCategoryLoadingIds: PropTypes.func.isRequired,
  setArticleLoadingIds: PropTypes.func.isRequired,
  setIsSyncingAll: PropTypes.func.isRequired
};

export default ZendeskModalManager;